import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Box, Flex } from "rebass/styled-components";
import { IoClose } from "react-icons/io5";
import videojs from "video.js";

import { ClipQualityIcon } from "MVVM/Components/Icons";
import { useClipQualityConfigs } from "MVVM/Hooks/useClipQualityConfigs";
import { useClipQualitySettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import { analytics } from "MVVM/Analytics";
import { IClip } from "MVVM/@types";

interface ClipQualityUpsellProps {
  clip: IClip;
  videoPlayerRef: React.MutableRefObject<videojs.Player | null>;
}

function parseHeight(resolution: string): number {
  const match = resolution.match(/(\d+)x(\d+)/);
  return match ? parseInt(match[2], 10) : 0;
}

const RESOLUTION_NAMES: Record<number, string> = {
  720: "HD",
  1080: "Full HD",
  1440: "2K",
  2160: "4K",
};

function qualityLabel(resolution: string, framerate: number): string {
  const height = parseHeight(resolution);
  const name = RESOLUTION_NAMES[height] ?? `${height}p`;
  return `${name} at ${framerate}fps`;
}

function findBestAvailableUpgrade(
  clipConfig: { framerate: number; resolution: string },
  configs: {
    configName: string;
    settings: { resolution: string; framerate: number };
    canApply: boolean;
  }[],
) {
  const clipHeight = parseHeight(clipConfig.resolution);
  const clipFps = clipConfig.framerate;

  let best: {
    resolution: string;
    framerate: number;
    canApply: boolean;
  } | null = null;

  for (const conf of configs) {
    const confHeight = parseHeight(conf.settings.resolution);
    const confFps = conf.settings.framerate;

    const isBetter =
      confHeight > clipHeight ||
      (confHeight === clipHeight && confFps > clipFps);
    if (!isBetter) continue;

    if (
      !best ||
      confHeight > parseHeight(best.resolution) ||
      (confHeight === parseHeight(best.resolution) && confFps > best.framerate)
    ) {
      best = {
        resolution: conf.settings.resolution,
        framerate: conf.settings.framerate,
        canApply: conf.canApply,
      };
    }
  }

  return best;
}

const UpsellContent = ({
  clip,
  upgrade,
  videoPlayerRef,
}: {
  clip: IClip;
  upgrade: { resolution: string; framerate: number; canApply: boolean };
  videoPlayerRef: React.MutableRefObject<videojs.Player | null>;
}) => {
  const [dismissed, setDismissed] = useState(false);
  const { open: openQualityModal } = useClipQualitySettingsModal();

  const currentQuality = qualityLabel(
    clip.clipQualityConfig!.resolution,
    clip.clipQualityConfig!.framerate,
  );
  const upgradeQuality = qualityLabel(upgrade.resolution, upgrade.framerate);

  useEffect(() => {
    analytics.track("Clip Quality Upsell - Impression", {
      currentQuality,
      upgradeQuality,
      canApply: upgrade.canApply,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (dismissed) return null;

  const label = qualityLabel(upgrade.resolution, upgrade.framerate);
  const message = upgrade.canApply
    ? `Your clips could be in ${label}`
    : `Upgrade for ${label} clips`;

  return (
    <Container
      onClick={(e) => {
        e.stopPropagation();
        analytics.track("Clip Quality Upsell - Clicked", {
          currentQuality,
          upgradeQuality,
          canApply: upgrade.canApply,
        });
        videoPlayerRef.current?.pause();
        openQualityModal();
      }}
    >
      <IconWrap>
        <ClipQualityIcon size={16} />
      </IconWrap>
      <Message>{message}</Message>
      <DismissButton
        onClick={(e) => {
          e.stopPropagation();
          analytics.track("Clip Quality Upsell - Dismissed", {
            currentQuality,
            upgradeQuality,
          });
          setDismissed(true);
        }}
      >
        <IoClose size={14} />
      </DismissButton>
    </Container>
  );
};

export const ClipQualityUpsell = ({
  clip,
  videoPlayerRef,
}: ClipQualityUpsellProps) => {
  const { configs } = useClipQualityConfigs();

  if (clip.pointOfView !== "OWNER") return null;
  if (!clip.clipQualityConfig) return null;

  const upgrade = findBestAvailableUpgrade(clip.clipQualityConfig, configs);
  if (!upgrade) return null;

  return (
    <UpsellContent
      clip={clip}
      upgrade={upgrade}
      videoPlayerRef={videoPlayerRef}
    />
  );
};

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled(Flex)`
  align-items: center;
  animation: ${slideUp} 0.3s ease-out;
  background: linear-gradient(
      135deg,
      rgba(0, 147, 168, 0.15),
      rgba(77, 189, 148, 0.15)
    ),
    ${({ theme }) => theme.colors.ned}ee;
  backdrop-filter: blur(8px);
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 999px;
  bottom: 56px;
  box-shadow: 0 0 12px rgba(0, 147, 168, 0.3);
  cursor: pointer;
  gap: 8px;
  padding: 8px 14px 8px 10px;
  position: absolute;
  right: 12px;
  transition:
    border-color 0.2s,
    transform 0.2s,
    box-shadow 0.2s;
  z-index: 5;

  &:hover {
    box-shadow: 0 0 18px rgba(0, 147, 168, 0.45);
    transform: scale(1.03);
  }
`;

const IconWrap = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
`;

const Message = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const DismissButton = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  margin-left: 2px;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;
